import { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Users, Loader2, Save } from 'lucide-react';
import CoverageRateAdjustments from '@/components/CoverageRateAdjustments';
import MerchantPlansPanel from '@/components/MerchantPlansPanel';
import { SelectMargen, SelectTarifaCobertura, notaMargen } from '@/components/NivelesDePrecio';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AP_LEVELS = ['AP1000','AP2500','AP5000','AP10000','AP20000','AP30000','AP40000','AP50000'];
const CHANNELS = ['retail','partner','wholesale'];
const CLASSES = ['A','B','C','D','E','Flat Rate'];
const RISKCHOICE = [
  { v: 'manual',           label: 'Manual — merchant picks per label' },
  { v: 'auto_smart',       label: 'Smart auto — $0-1k→Fair · $1k-10k→Share · >$10k→Declare' },
  { v: 'auto_selleshare',  label: 'Always SelleShare (falls back to Declare > $10k)' },
  { v: 'auto_sellefair',   label: 'Always SelleFair (falls back to Declare > $10k)' },
  { v: 'auto_selledeclare',label: 'Always SelleDeclare (Fair below $10k)' },
];
const WARRANTY = [
  { v: 'none', label: 'No warranty' },
  { v: 'SW',   label: 'SelleWarranty (1.00% base)' },
  { v: 'SWP',  label: 'SelleWarranty Plus (1.25% base)' },
];
const CARRIERS = ['USPS', 'UPS', 'FedEx', 'DHL'];

// The merchant setup Mike asked for (6-oct): a coverage limit and permissions
// per merchant, with the credit in the same place. Blank limit and every box
// ticked is what a merchant without setup has, which is how it was before.
const setupDraft = (m) => ({
  coverage_limit: m.setup?.coverage_limit ?? '',
  package_ceiling: m.setup?.package_ceiling ?? '',
  coverage_rate_cents: m.setup?.coverage_rate_cents ?? '',
  webship_markup_pct: m.setup?.webship_markup_pct ?? '',
  shipping_markup_pct: m.setup?.shipping_markup_pct ?? '',
  international_allowed: m.setup?.international_allowed !== false,
  local_delivery_allowed: m.setup?.local_delivery_allowed !== false,
  photo_fee_waived: m.setup?.photo_fee_waived === true,
  carriers: m.setup?.carriers_allowed ?? CARRIERS,
  plan_id: m.plan?.id ?? '',
  credit_authorized: !!m.credit?.authorized,
  credit_limit: m.credit?.limit ?? 1000,
  note: '',
});

const errorText = (j, fallback) => {
  const d = j && j.detail;
  if (!d) return fallback;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x.msg).join(' ');
  return d.message || fallback;
};

export default function AdminMerchantsPage() {
  const { token } = useAuth();
  const auth = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  const [merchants, setMerchants] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(null);
  const [setups, setSetups] = useState({});
  const [plans, setPlans] = useState([]);
  const [niveles, setNiveles] = useState(null);

  const load = async () => {
    const r = await fetch(`${API}/merchants/list`, { headers: auth });
    if (!r.ok) { toast.error('Admin only'); return; }
    const d = await r.json();
    const rp = await fetch(`${API}/merchants/plans`, { headers: auth });
    if (rp.ok) setPlans((await rp.json()).plans || []);
    else toast.error('Merchant plans could not be loaded.');
    const rn = await fetch(`${API}/merchants/pricing-levels`, { headers: auth });
    if (rn.ok) setNiveles(await rn.json());
    else toast.error('Margin and coverage levels could not be loaded.');
    setMerchants(d.merchants);
    const init = {};
    for (const m of d.merchants) init[m.id] = {
      ap_level: m.contract?.ap_level || 'AP1000',
      channel: m.contract?.channel || 'retail',
      pricing_class: m.contract?.pricing_class || 'C',
      commodities: (m.contract?.commodities || []).join(','),
      notes: m.contract?.notes || '',
      riskchoice_rule: m.contract?.riskchoice_rule || 'manual',
      warranty_default: m.contract?.warranty_default || 'none',
    };
    setDrafts(init);
    const s = {};
    for (const m of d.merchants) s[m.id] = setupDraft(m);
    setSetups(s);
  };

  const setS = (id, patch) => setSetups((all) => ({ ...all, [id]: { ...all[id], ...patch } }));

  const saveSetup = async (m) => {
    const s = setups[m.id];
    const before = setupDraft(m);
    setSaving(`setup-${m.id}`);
    try {
      const body = { note: s.note };
      const limit = s.coverage_limit === '' ? null : Number(s.coverage_limit);
      if (limit !== (m.setup?.coverage_limit ?? null)) {
        if (limit === null) body.clear_coverage_limit = true; else body.coverage_limit = limit;
      }
      const ceiling = s.package_ceiling === '' ? null : Number(s.package_ceiling);
      if (ceiling !== (m.setup?.package_ceiling ?? null)) {
        if (ceiling === null) body.clear_package_ceiling = true; else body.package_ceiling = ceiling;
      }
      const rate = s.coverage_rate_cents === '' ? null : Number(s.coverage_rate_cents);
      if (rate !== (m.setup?.coverage_rate_cents ?? null)) {
        if (rate === null) body.clear_coverage_rate = true; else body.coverage_rate_cents = rate;
      }
      const markup = s.webship_markup_pct === '' ? null : Number(s.webship_markup_pct);
      if (markup !== (m.setup?.webship_markup_pct ?? null)) {
        if (markup === null) body.clear_webship_markup = true; else body.webship_markup_pct = markup;
      }
      const margin = s.shipping_markup_pct === '' ? null : Number(s.shipping_markup_pct);
      if (margin !== (m.setup?.shipping_markup_pct ?? null)) {
        if (margin === null) body.clear_shipping_markup = true; else body.shipping_markup_pct = margin;
      }
      if (s.international_allowed !== before.international_allowed) body.international_allowed = s.international_allowed;
      if (s.local_delivery_allowed !== before.local_delivery_allowed) body.local_delivery_allowed = s.local_delivery_allowed;
      if (s.photo_fee_waived !== before.photo_fee_waived) body.photo_fee_waived = s.photo_fee_waived;
      if (s.carriers.join() !== before.carriers.join()) {
        if (s.carriers.length === CARRIERS.length) body.all_carriers = true; else body.carriers_allowed = s.carriers;
      }
      const setupChanged = Object.keys(body).length > 1;
      const credit = {};
      if (s.credit_authorized !== before.credit_authorized) credit.authorized = s.credit_authorized;
      if (Number(s.credit_limit) !== Number(before.credit_limit)) credit.limit = Number(s.credit_limit);
      const creditChanged = Object.keys(credit).length > 0;
      if (!setupChanged && !creditChanged) { toast.message('Nothing changed.'); return; }
      if (setupChanged) {
        const r = await fetch(`${API}/merchants/${m.id}/setup`, { method: 'POST', headers: auth, body: JSON.stringify(body) });
        const j = await r.json();
        if (!r.ok) throw new Error(errorText(j, 'Setup not saved.'));
      }
      if (creditChanged) {
        const r = await fetch(`${API}/webship/credit/accounts/${m.id}`, { method: 'POST', headers: auth, body: JSON.stringify({ ...credit, note: s.note }) });
        const j = await r.json();
        if (!r.ok) throw new Error(errorText(j, 'Credit not saved.'));
      }
      toast.success(`Setup saved for ${m.email}`);
      load();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(null); }
  };

  // Asignar (o sacar) un plan copia sus valores al setup, con la misma nota.
  const applyPlan = async (m) => {
    const s = setups[m.id];
    setSaving(`plan-${m.id}`);
    try {
      const r = await fetch(`${API}/merchants/${m.id}/plan`, { method: 'POST', headers: auth, body: JSON.stringify({ plan_id: s.plan_id || null, note: s.note }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(errorText(j, 'Plan not applied.'));
      toast.success(s.plan_id ? `Plan applied to ${m.email}` : `Plan removed from ${m.email}`);
      load();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(null); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async (id) => {
    setSaving(id);
    try {
      const d = drafts[id];
      const body = { ...d, commodities: d.commodities ? d.commodities.split(',').map(s => s.trim()).filter(Boolean) : [] };
      const r = await fetch(`${API}/merchants/${id}/contract`, { method: 'POST', headers: auth, body: JSON.stringify(body) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Save failed');
      toast.success('Contract saved');
      load();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(null); }
  };

  if (!merchants) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="admin-merchants-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight flex items-center gap-2">
          <Users className="w-6 h-6 text-indigo-500" /> Admin · Merchant Contracts
        </h1>
        <p className="text-slate-500 mt-1">Assign AP level, pricing channel, class and commodity whitelist per merchant.</p>
      </div>

      <CoverageRateAdjustments token={token} />

      <MerchantPlansPanel token={token} plans={plans} onChanged={load} niveles={niveles} />

      {merchants.map(m => (
        <Card key={m.id} data-testid={`merchant-card-${m.id.slice(0,8)}`}>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              {m.email} <span className="text-xs text-slate-400 font-mono">{m.id.slice(0,8)}…</span>
              {m.role === 'platform_admin' && <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-800">Admin</span>}
            </CardTitle>
            <CardDescription>Current: {m.contract?.ap_level || 'AP1000'} · {m.contract?.channel || 'retail'} · Class {m.contract?.pricing_class || 'C'}</CardDescription>
          </CardHeader>
          <CardContent className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <Label>AP Level</Label>
              <Select value={drafts[m.id]?.ap_level} onValueChange={v => setDrafts(d => ({ ...d, [m.id]: { ...d[m.id], ap_level: v } }))}>
                <SelectTrigger data-testid={`ap-${m.id.slice(0,8)}`}><SelectValue /></SelectTrigger>
                <SelectContent>{AP_LEVELS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Channel</Label>
              <Select value={drafts[m.id]?.channel} onValueChange={v => setDrafts(d => ({ ...d, [m.id]: { ...d[m.id], channel: v } }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CHANNELS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Class</Label>
              <Select value={drafts[m.id]?.pricing_class} onValueChange={v => setDrafts(d => ({ ...d, [m.id]: { ...d[m.id], pricing_class: v } }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CLASSES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Commodities (comma)</Label>
              <Input value={drafts[m.id]?.commodities || ''} onChange={e => setDrafts(d => ({ ...d, [m.id]: { ...d[m.id], commodities: e.target.value } }))} placeholder="jewelry, watches, electronics" />
            </div>
            <div className="lg:col-span-2">
              <Label>RiskChoice rule (auto-pick protection when merchant doesn't specify)</Label>
              <Select value={drafts[m.id]?.riskchoice_rule} onValueChange={v => setDrafts(d => ({ ...d, [m.id]: { ...d[m.id], riskchoice_rule: v } }))}>
                <SelectTrigger data-testid={`rule-${m.id.slice(0,8)}`}><SelectValue /></SelectTrigger>
                <SelectContent>{RISKCHOICE.map(r => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Warranty default</Label>
              <Select value={drafts[m.id]?.warranty_default} onValueChange={v => setDrafts(d => ({ ...d, [m.id]: { ...d[m.id], warranty_default: v } }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{WARRANTY.map(w => <SelectItem key={w.v} value={w.v}>{w.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button size="sm" onClick={() => save(m.id)} disabled={saving === m.id} className="w-full" data-testid={`save-merchant-${m.id.slice(0,8)}`}>
                {saving === m.id ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                Save
              </Button>
            </div>
            {setups[m.id] && (
              <div className="sm:col-span-2 lg:col-span-4 border-t pt-3 mt-1 space-y-3" data-testid={`setup-${m.id.slice(0,8)}`}>
                <p className="text-sm font-medium">Setup &amp; credit</p>
                <div className="flex flex-wrap items-end gap-2" data-testid={`plan-row-${m.id.slice(0,8)}`}>
                  <div>
                    <Label>Plan</Label>
                    <select className="block h-9 px-2 rounded-md border border-slate-200 dark:border-slate-700 dark:bg-slate-900 text-sm"
                      value={setups[m.id].plan_id} onChange={(e) => setS(m.id, { plan_id: e.target.value })} data-testid={`plan-select-${m.id.slice(0,8)}`}>
                      <option value="">No plan</option>
                      {plans.filter((p) => p.active || p.id === m.plan?.id).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => applyPlan(m)}
                    disabled={saving === `plan-${m.id}` || !setups[m.id].note.trim() || (!setups[m.id].plan_id && !m.plan)}
                    data-testid={`plan-apply-${m.id.slice(0,8)}`}>
                    {saving === `plan-${m.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : (!setups[m.id].plan_id ? 'Remove plan' : setups[m.id].plan_id === m.plan?.id ? 'Apply plan again' : 'Apply plan')}
                  </Button>
                  <p className="text-xs text-slate-500" data-testid={`plan-state-${m.id.slice(0,8)}`}>
                    {m.plan ? (m.plan.differs.length ? `On ${m.plan.name}, customized: ${m.plan.differs.join(', ').replaceAll('_', ' ')}` : `On ${m.plan.name}, as the plan`) : 'No plan'}
                    {' '}· applying a plan replaces the fields below; the note below is required.
                  </p>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <div>
                    <Label>Coverage limit without a code (USD)</Label>
                    <Input type="number" min="1" max={Number(setups[m.id].package_ceiling) || 25000} value={setups[m.id].coverage_limit}
                      onChange={(e) => setS(m.id, { coverage_limit: e.target.value })}
                      placeholder="Default: 2,500 general · 25,000 jewelry" data-testid={`s-limit-${m.id.slice(0,8)}`} />
                    <p className="text-xs text-slate-500 mt-1">Replaces the category limit. General goods never go above 5,000. Program caps still apply.</p>
                    {/* Mike, 15-oct: 50,000 for everyone, approved dealers up to 150,000 by manual approval.
                        24-oct: 25,000 per package until Lloyd's goes live (RESPUESTAS_MIKE.md §9.123); the
                        coverage tiers stop there too, so an approved ceiling does not raise it yet. */}
                    <Label className="mt-3 block">Approved package ceiling (USD)</Label>
                    <Input type="number" min="25001" max="150000" value={setups[m.id].package_ceiling}
                      onChange={(e) => setS(m.id, { package_ceiling: e.target.value })}
                      placeholder="Default: 25,000 per package" data-testid={`s-ceiling-${m.id.slice(0,8)}`} />
                    <p className="text-xs text-slate-500 mt-1">Manual approval, up to 150,000. Until Lloyd's goes live, coverage stops at 25,000 per package for every merchant.</p>
                    <Label className="mt-3 block">Coverage rate (¢ per $100)</Label>
                    <SelectTarifaCobertura niveles={niveles} value={setups[m.id].coverage_rate_cents}
                      onChange={(v) => setS(m.id, { coverage_rate_cents: v })} testid={`s-rate-${m.id.slice(0,8)}`} />
                    <p className="text-xs text-slate-500 mt-1">What this merchant pays for coverage. Commodity and value adjustments apply on top.</p>
                    <Label className="mt-3 block">Shipping margin</Label>
                    <SelectMargen niveles={niveles} value={setups[m.id].shipping_markup_pct}
                      onChange={(v) => setS(m.id, { shipping_markup_pct: v })} testid={`s-margin-${m.id.slice(0,8)}`} />
                    <p className="text-xs text-slate-500 mt-1">{notaMargen(niveles)}</p>
                    <Label className="mt-3 block">SelleGard / WebShip markup (%)</Label>
                    <Input type="number" min="0" max="50" step="0.5" value={setups[m.id].webship_markup_pct}
                      onChange={(e) => setS(m.id, { webship_markup_pct: e.target.value })}
                      placeholder="Default: 0" data-testid={`s-markup-${m.id.slice(0,8)}`} />
                    <p className="text-xs text-slate-500 mt-1">Extra on top of the WebShip price, which already includes your margin. Leave blank to bill the WebShip price as is.</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Permissions</Label>
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={setups[m.id].international_allowed} onChange={(e) => setS(m.id, { international_allowed: e.target.checked })} data-testid={`s-intl-${m.id.slice(0,8)}`} />
                      International
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={setups[m.id].local_delivery_allowed} onChange={(e) => setS(m.id, { local_delivery_allowed: e.target.checked })} data-testid={`s-local-${m.id.slice(0,8)}`} />
                      Local delivery
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={setups[m.id].photo_fee_waived} onChange={(e) => setS(m.id, { photo_fee_waived: e.target.checked })} data-testid={`s-photo-${m.id.slice(0,8)}`} />
                      Item photo free (waive the 1.99)
                    </label>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Carriers</Label>
                    {CARRIERS.map((c) => (
                      <label key={c} className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={setups[m.id].carriers.includes(c)}
                          onChange={(e) => setS(m.id, { carriers: e.target.checked
                            ? CARRIERS.filter((x) => x === c || setups[m.id].carriers.includes(x))
                            : setups[m.id].carriers.filter((x) => x !== c) })}
                          data-testid={`s-carrier-${c}-${m.id.slice(0,8)}`} />
                        {c}
                      </label>
                    ))}
                  </div>
                  <div className="space-y-1.5">
                    <Label>SelleGard credit</Label>
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={setups[m.id].credit_authorized} onChange={(e) => setS(m.id, { credit_authorized: e.target.checked })} data-testid={`s-credit-${m.id.slice(0,8)}`} />
                      Authorized
                    </label>
                    <Input type="number" min="0" step="0.01" value={setups[m.id].credit_limit} onChange={(e) => setS(m.id, { credit_limit: e.target.value })} data-testid={`s-credit-limit-${m.id.slice(0,8)}`} />
                    {m.credit && !m.credit.kys_approved && <p className="text-xs text-amber-700" data-testid={`s-kys-missing-${m.id.slice(0,8)}`}>KYS not approved: credit cannot be granted</p>}
                    {m.credit?.frozen && <p className="text-xs text-rose-600">On hold: {m.credit.frozen_reason_text}</p>}
                    {m.credit?.authorized && <p className="text-xs text-slate-500">Owed ${Number(m.credit.outstanding).toFixed(2)} · available ${Number(m.credit.available).toFixed(2)}</p>}
                  </div>
                </div>
                <div className="flex flex-wrap items-end gap-3">
                  <div className="flex-1 min-w-[16rem]">
                    <Label>Why (required, it is logged)</Label>
                    <Input value={setups[m.id].note} onChange={(e) => setS(m.id, { note: e.target.value })} placeholder="e.g. verified dealer, approved by the office" data-testid={`s-note-${m.id.slice(0,8)}`} />
                  </div>
                  <Button size="sm" onClick={() => saveSetup(m)} disabled={saving === `setup-${m.id}` || !setups[m.id].note.trim()} data-testid={`s-save-${m.id.slice(0,8)}`}>
                    {saving === `setup-${m.id}` ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                    Save setup
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
